import { graphql } from 'react-relay';
import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';

import { expValEquals } from '@confluence/feature-experiments';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useAddCommentPermissionCheckPermissionCheckAddCommentQuery as useAddCommentPermissionCheckPermissionCheckAddCommentQuery$data,
	useAddCommentPermissionCheckPermissionCheckAddCommentQueryVariables as useAddCommentPermissionCheckPermissionCheckAddCommentQuery$variables,
} from './__types__/useAddCommentPermissionCheckPermissionCheckAddCommentQuery.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
const useAddCommentPermissionCheckPermissionCheckAddCommentQuery = graphql`
	query useAddCommentPermissionCheckPermissionCheckAddCommentQuery(
		$contentId: ID!
		$status: [String]
	) {
		content(id: $contentId, status: $status) {
			nodes {
				...ContentOperationsFragment @relay(mask: false)
			}
		}
	}
`;

type RefetchFunction = () => Promise<unknown>;

const noOpRefetch: RefetchFunction = () => Promise.resolve();

type PermissionCheckQueryResult = {
	data?: useAddCommentPermissionCheckPermissionCheckAddCommentQuery$data | null;
	loading: boolean;
	error?: ApolloErrorInterop;
	refetch?: RefetchFunction;
	networkStatus?: NetworkStatus;
};

export const useAddCommentPermissionCheck = (
	contentId: string,
	cacheOnly = false,
): {
	canAddComments: boolean;
	loading: boolean;
	contentQueryError: ApolloErrorInterop | undefined;
	refetch: RefetchFunction;
	networkStatus: NetworkStatus;
} => {
	const baseOptions = {
		skip: !contentId,
	};

	let queryResult: PermissionCheckQueryResult;

	if (expValEquals('confluence_frontend_stop_watching_queries', 'isEnabled', true)) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		queryResult = useQueryNoFurtherUpdateInterop<
			useAddCommentPermissionCheckPermissionCheckAddCommentQuery$data,
			useAddCommentPermissionCheckPermissionCheckAddCommentQuery$variables
		>(
			useAddCommentPermissionCheckPermissionCheckAddCommentQuery,
			expValEquals('confluence_content_operations_cache_first', 'cohort', 'experiment')
				? {
						...baseOptions,
						variables: {
							contentId,
							status: ['draft', 'current'],
						},
						skipFetchIfCached: true,
					}
				: {
						...baseOptions,
						variables: {
							contentId,
							status: ['draft', 'current'],
						},
						skipFetchIfCached: false,
					},
		);
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		queryResult = useQueryInterop<
			useAddCommentPermissionCheckPermissionCheckAddCommentQuery$data,
			useAddCommentPermissionCheckPermissionCheckAddCommentQuery$variables
		>(
			useAddCommentPermissionCheckPermissionCheckAddCommentQuery,
			expValEquals('confluence_content_operations_cache_first', 'cohort', 'experiment')
				? {
						...baseOptions,
						variables: {
							contentId,
							status: ['draft', 'current'],
						},
						notifyOnNetworkStatusChange: true,
						fetchPolicy: cacheOnly ? ('cache-only' as const) : ('cache-first' as const),
					}
				: {
						...baseOptions,
						variables: {
							contentId,
							status: ['draft', 'current'],
						},
						notifyOnNetworkStatusChange: true,
						fetchPolicy: cacheOnly ? ('cache-only' as const) : ('cache-and-network' as const),
					},
		);
	}

	const { data, loading, error } = queryResult;
	const networkStatus =
		'networkStatus' in queryResult && queryResult.networkStatus !== undefined
			? queryResult.networkStatus
			: NetworkStatus.ready;
	const refetch =
		'refetch' in queryResult && typeof queryResult.refetch === 'function'
			? queryResult.refetch
			: noOpRefetch;

	const contentNode = data?.content?.nodes?.[0];
	const operations = contentNode?.operations ?? [];

	const createPermission = operations.some(
		(operationResults) =>
			operationResults?.operation === 'create' && operationResults?.targetType === 'comment',
	);

	return {
		canAddComments: createPermission,
		loading,
		contentQueryError: error,
		refetch,
		networkStatus,
	};
};
